import React, { useCallback, useEffect, useState } from "react";
import { Paper } from "@material-ui/core";
import { useAlert } from "react-alert";
import { Modal, Spinner, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import SVG from "react-inlinesvg";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { baseUrl } from "../../../services/config";
import { PremissionChecker } from "../../../services/PremissionChecker";

function DefineCRMInquirySource() {
  const alert = useAlert();
  const user = useSelector(state => state.user.user);
  const authtoken = localStorage.getItem("userToken");
  const [inquirySources, setInquirySources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modal, setModal] = useState(null);
  const [name, setName] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const showErrors = useCallback(
    (data, fallback) => {
      const message = data?.metadata?.message;
      if (message && typeof message === "object") {
        Object.values(message).forEach(item => alert.error(item));
      } else {
        alert.error(message || fallback);
      }
    },
    [alert]
  );

  const getInquirySources = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${baseUrl}/crm-inquiry-sources`, {
        headers: { Authorization: `Bearer ${authtoken}` }
      });
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to load CRM inquiry sources");
        return;
      }
      setInquirySources(data?.payload || []);
    } catch (error) {
      alert.error("Unable to load CRM inquiry sources");
    } finally {
      setLoading(false);
    }
  }, [alert, authtoken, showErrors]);

  useEffect(() => {
    getInquirySources();
  }, [getInquirySources]);

  const closeModal = () => {
    setModal(null);
    setName("");
    setSelectedId(null);
  };

  const saveInquirySource = async () => {
    if (!name.trim()) {
      alert.error("Inquiry Source Name Is Required");
      return;
    }

    const isEditing = modal === "edit";
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiry-sources${isEditing ? `/${selectedId}` : ""}`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${authtoken}`
          },
          body: JSON.stringify({ name: name.trim() })
        }
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(
          data,
          `Unable to ${isEditing ? "update" : "add"} inquiry source`
        );
        return;
      }
      alert.success(
        `Inquiry source successfully ${isEditing ? "updated" : "added"}`
      );
      closeModal();
      getInquirySources();
    } catch (error) {
      alert.error(`Unable to ${isEditing ? "update" : "add"} inquiry source`);
    } finally {
      setSubmitting(false);
    }
  };

  const deleteInquirySource = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(
        `${baseUrl}/crm-inquiry-sources/${selectedId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${authtoken}` }
        }
      );
      const data = await response.json();
      if (!response.ok || data?.metadata?.success === false) {
        showErrors(data, "Unable to delete inquiry source");
        return;
      }
      alert.success(data?.payload || "Inquiry source successfully deleted");
      closeModal();
      getInquirySources();
    } catch (error) {
      alert.error("Unable to delete inquiry source");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper style={{ marginTop: "10px", marginBottom: "10px", padding: "15px" }}>
      {PremissionChecker(user, "crm_inquiry_source.create") && (
        <button
          className="btn btn-primary mr-2"
          onClick={() => setModal("add")}
        >
          Add CRM Inquiry Source
        </button>
      )}

      <Table className="mt-5">
        <thead>
          <tr className="text-center">
            <th>ID</th>
            <th>Inquiry Source Name</th>
            {PremissionChecker(user, "crm_inquiry_source.update") && (
              <th>Update</th>
            )}
            {PremissionChecker(user, "crm_inquiry_source.delete") && (
              <th>Delete</th>
            )}
          </tr>
        </thead>
        <tbody>
          {inquirySources.map(inquirySource => (
            <tr className="text-center" key={inquirySource.id}>
              <td>{inquirySource.id}</td>
              <td>{inquirySource.name}</td>
              {PremissionChecker(user, "crm_inquiry_source.update") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-primary-custom btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(inquirySource.id);
                      setName(inquirySource.name || "");
                      setModal("edit");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-primary-custom">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Write.svg"
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
              {PremissionChecker(user, "crm_inquiry_source.delete") && (
                <td>
                  <button
                    type="button"
                    className="btn btn-icon btn-light btn-hover-danger btn-sm mx-3"
                    onClick={() => {
                      setSelectedId(inquirySource.id);
                      setModal("delete");
                    }}
                  >
                    <span className="svg-icon svg-icon-md svg-icon-danger">
                      <SVG
                        src={toAbsoluteUrl(
                          "/media/svg/icons/Communication/Delete-user.svg"
                        )}
                      />
                    </span>
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>

      {loading && (
        <div className="row justify-content-center">
          <Spinner animation="border" />
        </div>
      )}

      <Modal
        show={modal === "add" || modal === "edit"}
        onHide={closeModal}
        size="md"
      >
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            {modal === "edit" ? "Update" : "Add New"} CRM Inquiry Source
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="form-control-label">
            <b>
              Enter Inquiry Source Name <span style={{ color: "red" }}>*</span>
            </b>
          </label>
          <input
            className="form-control"
            value={name}
            onChange={event => setName(event.target.value)}
            placeholder="Enter Inquiry Source Name"
          />
        </Modal.Body>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button
              className="btn btn-primary w-100px"
              onClick={saveInquirySource}
            >
              {modal === "edit" ? "Update" : "Add"}
            </button>
          )}
          <button className="btn btn-danger" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>

      <Modal show={modal === "delete"} onHide={closeModal}>
        <Modal.Header className="modal_header_style" closeButton>
          <Modal.Title className="Modal_Heading_font">
            Are you sure you want to delete this CRM inquiry source?
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer>
          {submitting ? (
            <Spinner animation="border" />
          ) : (
            <button className="btn button_blue" onClick={deleteInquirySource}>
              Delete
            </button>
          )}
          <button className="btn btn-danger btn_width" onClick={closeModal}>
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </Paper>
  );
}

export default DefineCRMInquirySource;
